import { PdfBrandConfig } from '@common/types';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { LoaderOverlay, Stack, Typography } from '@common-components';
import { FormMode } from 'enums';
import { useMount, useMutatePdf, useToast } from 'hooks';
import { useMutateBoxItems } from 'hooks/api/box';
import { messages } from 'i18n';
import { PartialSubmission } from 'types';
import { logger } from 'utils';
import Banner, { BannerMode } from 'broker/components/Banner';
import ContentPreview from 'broker/components/box/ContentPreview';
import useSetFilesExplorerViewMode from 'broker/pages/SubmissionWorkspacePage/components/NestedViews/Quote/components/useSetFilesExplorerViewMode';
import { QuoteFormCreate } from 'broker/pages/SubmissionWorkspacePage/components/NestedViews/Quote/form-methods/types';
import { FlowGenerationStatus } from 'broker/pages/SubmissionWorkspacePage/components/NestedViews/Quote/types';
import { EncryptedFileError } from 'broker/pages/SubmissionWorkspacePage/utils/pdf-creator/usePDFCreateAndCombine';
import { useGetFlowQuotePdfGenerator } from './pdf-creator/use-flow-quote-pdf-generator';

interface FlowQuoteProps {
  mode: FormMode;
  submission: PartialSubmission;
  marketQuoteFileIds: string[];
  marketName: string;
  flowQuoteIds: string[];
  setFlowQuoteIds: (flowQuoteId: string[]) => void;
  flowGenerationStatus: FlowGenerationStatus;
  setFlowGenerationStatus: (flowGenerationStatus: FlowGenerationStatus) => void;
  submitForm: (saveAsDoc?: boolean) => Promise<string | undefined>;
  isDirty: boolean;
  onClose: () => void;
  brandConfig: PdfBrandConfig;
}

export default function FlowQuote({
  mode,
  submission,
  marketQuoteFileIds,
  marketName,
  setFlowQuoteIds,
  flowQuoteIds,
  flowGenerationStatus,
  setFlowGenerationStatus,
  brandConfig,
  submitForm,
  onClose,
}: FlowQuoteProps) {
  const quoteMethods = useFormContext();
  const { deleteFile } = useMutateBoxItems();
  const values = quoteMethods.getValues() as QuoteFormCreate;
  const getFlowQuotePdfGenerator = useGetFlowQuotePdfGenerator();
  const hasError = flowGenerationStatus === FlowGenerationStatus.Error;
  // undefined means error
  const [errorMessage, setErrorMessage] = useState('');
  const { decryptPdf } = useMutatePdf();
  useSetFilesExplorerViewMode(true, mode);

  useEffect(() => {
    if (errorMessage && !hasError) {
      setErrorMessage('');
    }
  }, [errorMessage, hasError]);

  const removeDecryptionFromFile = useCallback(
    async (fileDetails: { fileId: string; fileName: string }) => {
      logger.log('info', {
        message: 'Encrypted File - Attempting to Decrypt',
        ...fileDetails,
      });
      await decryptPdf.mutateAsync({
        boxItemId: fileDetails.fileId,
        folderId: submission.boxFolderId,
      });
      logger.log('info', { message: 'Successfully decrypted file', ...fileDetails });
    },
    [decryptPdf, submission.boxFolderId],
  );

  const { showToast } = useToast();

  const saveFormOnFlowQuoteError = async () => {
    setFlowGenerationStatus(FlowGenerationStatus.Loading);
    showToast('error', {
      message: messages.addQuotePage.flowQuoteError.title,
    });
    await submitForm();
    onClose();
  };

  const createFlowQuote = async () => {
    if (mode === FormMode.create || mode === FormMode.edit) {
      setFlowGenerationStatus(FlowGenerationStatus.Loading);
      if (mode === FormMode.create) {
        // Delete the flow quote if it exists - can happen if you go back to the previous step and then come back (in this case agreed with product that we will create flow quote pdf again, as user may have changed data in the form)
        // for update we won't delete the file as it can have been used in the submission for other things, and also if the user chooses not to update after the flow quote regenerates,  the previous flow quote are used in that quote and if we delete them from box it will be a problem
        if (flowQuoteIds.length > 0) {
          await Promise.all(flowQuoteIds.map((flowQuoteId) => deleteFile.mutateAsync({ fileId: flowQuoteId })));
          setFlowQuoteIds([]);
        }
      }
      getFlowQuotePdfGenerator({
        submission,
        values,
        marketName,
        marketQuoteFileIds,
        brandConfig,
      })
        .then(async (flowQuoteBoxItem) => {
          if (flowQuoteBoxItem) {
            setFlowQuoteIds([flowQuoteBoxItem.id]);
            setFlowGenerationStatus(FlowGenerationStatus.Idle);
          } else {
            logger.log('error', { message: 'generateAndUploadFlowPDF has ended with no boxItem', flowQuoteBoxItem });
            setFlowGenerationStatus(FlowGenerationStatus.Error);
            await saveFormOnFlowQuoteError();
          }
        })
        .catch(async (e) => {
          if (e instanceof EncryptedFileError) {
            const fileDetails = { fileId: e.fileId, fileName: e.fileName };
            try {
              await removeDecryptionFromFile(fileDetails);
              await createFlowQuote();
            } catch (_e) {
              logger.log('error', { message: 'Failed to decrypt file', ...fileDetails });
              setFlowGenerationStatus(FlowGenerationStatus.Error);
              setErrorMessage(messages.addQuotePage.flowQuoteError.getEncryptedFileError(fileDetails.fileName));
              await saveFormOnFlowQuoteError();
              throw _e;
            }
          } else {
            setFlowGenerationStatus(FlowGenerationStatus.Error);
            await saveFormOnFlowQuoteError();
            throw e;
          }
        });
    }
  };

  // Execute the PDF download on component mount
  useMount(() => {
    createFlowQuote();
  });

  const getContent = () => {
    if (hasError) {
      return (
        <Stack mt={2}>
          <Banner
            title={messages.addQuotePage.flowQuoteError.title}
            subtitle={errorMessage || messages.addQuotePage.flowQuoteError.defaultDescription}
            mode={BannerMode.Error}
          />
        </Stack>
      );
    }
    if (flowGenerationStatus === FlowGenerationStatus.Loading) {
      return <LoaderOverlay hideOverlay />;
    }
    return flowQuoteIds ? <ContentPreview fileId={flowQuoteIds[0]} hasHeader={false} sx={{ height: 1 }} /> : null;
  };

  return (
    <Stack height={1} py={4} px={12}>
      <Stack ml={hasError ? 0 : 2} mb={hasError ? 1 : 3}>
        <Typography variant="body1Bold">{messages.addQuotePage.flowQuoteTitle}</Typography>
      </Stack>
      {getContent()}
    </Stack>
  );
}
